import {
    MessageCircle,
    Database,
    Server,
    Users,
    Heart,
    Lightbulb,
    Shield,
    GraduationCap
} from "lucide-react"

export function OurApproach() {
    return (
        <section id="our-approach" className="py-24 md:py-32 bg-card">
            <div className="max-w-6xl mx-auto px-6">
                {/* Section Header */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <p className="text-sm font-medium text-forest uppercase tracking-wider mb-4">
                        Our Approach
                    </p>
                    <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-6 text-balance">
                        A community-owned intelligence engine for local revitalisation
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                        We're building more than a directory. Corkboard is a hyper-local knowledge graph
                        that bridges word-of-mouth and modern AI to bring life back to local high streets.
                    </p>
                </div>

                {/* Problem + Mission */}
                <div className="grid md:grid-cols-2 gap-8 mb-16">
                    <div className="bg-cream rounded-2xl p-8 border border-border">
                        <div className="w-12 h-12 rounded-xl bg-amber/20 text-amber flex items-center justify-center mb-6">
                            <Lightbulb className="w-6 h-6" />
                        </div>
                        <h3 className="font-semibold text-xl text-foreground mb-4">The Problem We're Solving</h3>
                        <p className="text-muted-foreground leading-relaxed">
                            Local knowledge is fragmented. Events get shared on scattered social media posts,
                            community noticeboards go unnoticed, and independent businesses struggle to reach
                            the people right on their doorstep. Meanwhile, big platforms extract data without
                            giving back to the communities they serve.
                        </p>
                    </div>

                    <div className="bg-cream rounded-2xl p-8 border border-border">
                        <div className="w-12 h-12 rounded-xl bg-forest/20 text-forest flex items-center justify-center mb-6">
                            <Heart className="w-6 h-6" />
                        </div>
                        <h3 className="font-semibold text-xl text-foreground mb-4">Our Mission</h3>
                        <p className="text-muted-foreground leading-relaxed">
                            To create a community-owned platform that connects residents, retailers, and
                            visitors through local offers, events, and experiences. We want to make discovering
                            what's happening locally as easy as asking a neighbour, while ensuring the technology
                            serves the community rather than extracting from it.
                        </p>
                    </div>
                </div>

                {/* Technical Approach */}
                <div className="mb-16">
                    <h3 className="font-serif text-2xl font-semibold text-foreground text-center mb-10">
                        Our Technical Approach
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <TechCard
                            icon={<MessageCircle className="w-6 h-6" />}
                            title="WhatsApp Intake"
                            description="Low-friction entry for busy retailers and organisers. Send event details via chat and our AI handles the rest."
                        />
                        <TechCard
                            icon={<Database className="w-6 h-6" />}
                            title="RAG-Powered Discovery"
                            description="A knowledge graph that stays fresh. Content automatically expires based on time-to-live, keeping results relevant."
                        />
                        <TechCard
                            icon={<Server className="w-6 h-6" />}
                            title="Local-First Infrastructure"
                            description="Small-scale models hosted on community-controlled hardware. Your data stays in your community."
                        />
                        <TechCard
                            icon={<Shield className="w-6 h-6" />}
                            title="Responsible AI"
                            description="Transparent, explainable, and privacy-respecting. No black boxes, no surveillance capitalism."
                        />
                    </div>
                </div>

                {/* Community Strategy */}
                <div className="bg-forest rounded-3xl p-8 md:p-12 text-cream">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h3 className="font-serif text-2xl md:text-3xl font-semibold mb-6">
                                Boots on the Ground
                            </h3>
                            <p className="text-cream/80 leading-relaxed mb-6">
                                Technology alone won't revitalise a high street. Success depends on trust,
                                built through genuine community engagement.
                            </p>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-cream/10 flex items-center justify-center shrink-0 mt-0.5">
                                        <Users className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="font-semibold mb-1">Direct Outreach</p>
                                        <p className="text-cream/70 text-sm">Door-to-door work with local businesses to onboard them to retailer tools.</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-cream/10 flex items-center justify-center shrink-0 mt-0.5">
                                        <GraduationCap className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="font-semibold mb-1">Maintainer Workshops</p>
                                        <p className="text-cream/70 text-sm">Regular sessions teaching local youth about cloud, APIs, and AI. Technical maintenance becomes a community educational asset.</p>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div className="flex justify-center">
                            <div className="bg-cream/10 rounded-2xl p-6 max-w-xs">
                                <p className="text-sm text-cream/60 uppercase tracking-wider mb-2">Our Commitment</p>
                                <p className="font-serif text-xl font-semibold mb-4">
                                    Community wellbeing through digital literacy
                                </p>
                                <p className="text-cream/70 text-sm leading-relaxed">
                                    By involving students and volunteers in the tech stack, we promote digital
                                    skills alongside economic growth. The platform belongs to those who maintain it.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

function TechCard({
    icon,
    title,
    description,
}: {
    icon: React.ReactNode
    title: string
    description: string
}) {
    return (
        <div className="bg-cream rounded-2xl p-6 border border-border/50 hover:border-forest/30 hover:shadow-lg transition-all duration-300">
            <div className="w-12 h-12 rounded-xl bg-forest/10 text-forest flex items-center justify-center mb-5">
                {icon}
            </div>
            <h4 className="font-semibold text-foreground mb-2">{title}</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </div>
    )
}
